import React, { useContext, useState } from "react"
import Loader from "../ui/loader"
import moment from "moment"
import { Badge } from "../ui/badge"
import { getOrdinal } from "../../utils/get-ordinal"
import { Label } from "../ui/label"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@radix-ui/react-popover"
import { ServiceRequests } from "../../api/app.service"
import { ServiceTypes } from "../../data/service-types"
import { SvgUsersSolid } from "../icons"
import { Switch } from "../ui/switch"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"


interface Props {
  service: any
}

const ServiceCardBannerOverlay: React.FC<Props> = ({ service }) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { service_details, is_group_service } = service
  const { type, isGroupService } = service_details
  let { Icon, title, description } = ServiceTypes[type]
  const [isServiceVisible, setIsServiceVisible] = useState(
    service_details?.visibility?.profile?.service
  )
  const [loadingToggleChange, setLoadingToggleChange] = useState<boolean>(false)
  const isEditWebsitePage = window.location.pathname.includes(
    "/coach/edit-website"
  )

  if (isGroupService) {
    title = "Group Session"
    description =
      "This is a group coaching program where multiple clients can join and participate together."
    Icon = SvgUsersSolid
  }

  const getStartDateMessage = (): string | undefined => {
    const group = service_details.group
    if (!group) return

    const userTz = moment.tz.guess()

    if (group.startDate) {
      return `Starts on ${moment
        .tz(group.startDate, userTz)
        .format("MMM DD, YYYY")}`
    }

    const meetingDates = group.meetingDates || []
    const sessionCount = meetingDates.length

    if (sessionCount === 0) {
      return "No sessions available"
    } else {
      const date = meetingDates[0]
      const isPast = new Date(date) < new Date()
      const formattedDate = moment
        .tz(date, userTz)
        .format("MMM DD [at] h:mm A z")
      const sessionLabel = getOrdinal(1)
      const sessionStatus = isPast ? "was" : "Scheduled for"
      return `${sessionLabel} Session ${sessionStatus} ${formattedDate}`
    }
  }

  const startDateMessage = getStartDateMessage()

  const updateServiceProfileVisibility = () => {
    if (user) {
      setLoadingToggleChange(true)
      const updatedVisibility = !isServiceVisible
      setIsServiceVisible(updatedVisibility)
      const serviceObject = {
        ...service_details,
        visibility: {
          ...service_details.visibility,
          profile: {
            ...service_details.visibility.profile,
            service: updatedVisibility,
          },
        },
        image: {
          ...service_details.image,
          base64: "",
          base64extension: "",
        },
      }
      ServiceRequests.updateClientService({
        user_id: parseInt(user.user_id),
        stripe_product_id: service.stripe_product_id,
        stripe_price_id: service.stripe_price_id,
        service: serviceObject,
        group_service_id: service?.group_service_id,
        payment_term: service?.payment_term
      })
        .then(() => {
          setLoadingToggleChange(false)
          toast.success(
            updatedVisibility
              ? `Your service, '${service_details?.title}', is now public!`
              : `Your service, '${service_details?.title}', is now private!`
          )
        })
        .catch((ex: any) => {
          toast.error(ex.response?.data?.message)
        })
    }
  }

  return (
    <Popover>
      <div className=" flex flex-col items-start gap-2 p-4 left-4">
        <PopoverTrigger
          onClick={(e) => e.stopPropagation()}
          className="w-full -mt-1"
        >
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                <Icon className="mr-1" />
                {title}
              </Badge>
            </div>

            {isEditWebsitePage && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1"
              >
                <Label htmlFor="service-visible" className="mr-1 font-bold">
                  {isServiceVisible ? "Public" : "Private"}
                </Label>
                {loadingToggleChange ? (
                  <Loader />
                ) : (
                  <Switch
                  variant="outline"
                    id="service-visible"
                    checked={isServiceVisible}
                    onCheckedChange={(checked) => {
                      setIsServiceVisible(checked)
                      updateServiceProfileVisibility()
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {is_group_service && startDateMessage && (
            <div
              className={`mb-1 w-max ${
                startDateMessage.length > 22 ? "-ml-2.5" : "sm:-ml-0"
              }`}
            >
              <Badge variant="success">{startDateMessage}</Badge>
            </div>
          )}
        </PopoverTrigger>
      </div>
      <PopoverContent
        side="bottom"
        className="flex flex-col items-center rounded-xl gap-3 bg-[#F8F8F8] p-3"
      >
        <Icon className="h-6 w-6 mx-auto" />
        <h3 className="font-bold text-[16px] md:text-[18px] md:mb-[8px] text-center">
          {title}
        </h3>
        <p className="md:text-center w-[310px] text-base text-graySlate mb-[16px] md:mb-[24px]">
          {description}
        </p>
      </PopoverContent>
    </Popover>
  )
}

export default ServiceCardBannerOverlay
